import { useCallback, useEffect, useRef, useState } from 'react'
import { CloudDownloadIcon, themeClubbi } from 'clubbi-ui'
import { toJpeg } from 'html-to-image'

import { DetailsFlyerInput } from '../molecules/flyer/DetailsFlyerInput'
import { Flyer } from '../molecules/flyer/Flyer'
import { FullScreenImage } from '../molecules/flyer/FullScreenImage'
import { SalesHeader } from '../molecules/flyer/SalesHeader'
import gtm from '../../common/gtmTracker'
import { useAppSelector } from '../../common/hooks'
import { currentDateString, sortProducts } from '../../common/utils'
import { Product } from '../../store/data/types'
import { getMerchantData } from '../../services/merchantApi'
import { getSalesFlyerProducts } from '../../services/productApi'

import {
  StyledContainerFlyer,
  StyledDescription,
  StyledImageSalesFlyer,
  StyledMainContainer,
  StyledNotFound,
  StyledPreview,
  StyledSaveButton,
  StyledSpinner,
  StyledTitle,
} from './styles/SalesFlyer.style'

export const SalesFlyer = () => {
  const {
    merchantCodeInput,
    eans,
    products: productsInput,
    editedPrices,
  } = useAppSelector((state) => state.salesFlyer)
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [flyerImageUrl, setFlyerImageUrl] = useState<string>('')
  const [fullScreenImage, setFullScreenImage] = useState<boolean>(false)
  const [productsNotFound, setProductsNotFound] = useState<string[]>([])
  const ref = useRef<HTMLDivElement>(null)

  const disableGetButton = !merchantCodeInput || !eans.length
  const showFlyer = loading && !!products.length
  const showSaveButton = !!flyerImageUrl && !showFlyer

  useEffect(() => {
    if (showFlyer) {
      setTimeout(() => {
        generateImageUrl()
      }, 6000)
    }
  }, [showFlyer])

  const generateImageUrl = useCallback(async () => {
    if (ref?.current) {
      const dataUrl = await toJpeg(ref.current!)
      setFlyerImageUrl(dataUrl)
      setLoading(false)
    }
  }, [ref])

  const getFlyerProducts = async () => {
    setLoading(true)
    setFlyerImageUrl('')

    try {
      const [{ websiteRegionId }] = await getMerchantData(merchantCodeInput!)

      const dataProducts = await getSalesFlyerProducts(
        merchantCodeInput!,
        websiteRegionId!,
        eans.join(',')
      )

      const notFoundEans = eans.filter((ean) => !dataProducts.some((p) => p.ean === ean))
      setProductsNotFound(notFoundEans)

      if (dataProducts.length === 0) {
        setLoading(false)
        return
      }

      const sortedProducts = sortProducts(dataProducts, eans)

      const finalProducts = sortedProducts.map((product) => {
        const editedPrice = editedPrices[product.ean]
        if (!editedPrice) return product

        return {
          ...product,
          supplierPrices: [
            {
              ...product.supplierPrices[0],
              price: Number(editedPrice),
              packagePrice: Number(editedPrice) * (product.packageNumberOfItems || 1),
            },
          ],
        }
      })

      setProducts(finalProducts)
    } catch (error) {
      console.error('Erro ao gerar encarte:', error)
      setLoading(false)
    }
  }

  const saveImage = () => {
    const link = document.createElement('a')
    link.download = `sales-flyer${currentDateString}`
    link.href = flyerImageUrl
    link.click()
    gtm.triggerSaveSalesFlyer()
  }

  return (
    <StyledMainContainer>
      <SalesHeader />
      <DetailsFlyerInput
        getFlyerProducts={getFlyerProducts}
        disabled={disableGetButton}
        products={productsInput}
      />
      <StyledPreview>
        <StyledDescription>
          <StyledTitle>2. Pré-visualizar</StyledTitle>
          {showSaveButton && (
            <StyledSaveButton onClick={saveImage} data-testid="save-img-button">
              <CloudDownloadIcon
                color={themeClubbi.palette.brandPurple[60]}
                size={themeClubbi.typography.fontSizeM}
              />
              Salvar
            </StyledSaveButton>
          )}
        </StyledDescription>
        {showSaveButton && (
          <StyledImageSalesFlyer
            src={flyerImageUrl}
            alt="sales-flyer"
            onClick={() => setFullScreenImage(true)}
          />
        )}
        {productsNotFound.length > 0 && (
          <StyledNotFound>
            {productsNotFound.length === 1
              ? `Produto não encontrado: ${productsNotFound[0]}`
              : `Produtos não encontrados: ${productsNotFound.join(', ')}`}
          </StyledNotFound>
        )}
      </StyledPreview>
      {loading && <StyledSpinner />}
      {showFlyer && (
        <StyledContainerFlyer ref={ref}>
          <Flyer products={products} />
        </StyledContainerFlyer>
      )}
      {fullScreenImage && (
        <FullScreenImage
          flyerImageUrl={flyerImageUrl}
          fullScreenImage={fullScreenImage}
          setFullScreenImage={setFullScreenImage}
        />
      )}
    </StyledMainContainer>
  )
}
